import React, { useState, useEffect } from 'react';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { getFollowups, getConversations, updateFollowupStatus } from '../../lib/api';
import { useProperty } from '../../context/PropertyContext';
import { Clock, Check, X, Calendar } from 'lucide-react';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import { toast } from 'sonner';

const columns = [
  { id: 'NEW', label: 'New', color: 'bg-blue-500' },
  { id: 'PENDING', label: 'Waiting', color: 'bg-amber-500' },
  { id: 'FOLLOWUP_1', label: 'Follow-up #1', color: 'bg-orange-500' },
  { id: 'FOLLOWUP_2', label: 'Follow-up #2', color: 'bg-red-500' },
  { id: 'CLOSE_LOOP', label: 'Close-the-loop', color: 'bg-purple-500' },
  { id: 'BOOKED', label: 'Booked', color: 'bg-green-500' },
  { id: 'LOST', label: 'Lost', color: 'bg-stone-500' },
];

export const Followups = () => {
  const { properties, selectedPropertyId, getPropertyName } = useProperty();
  const [loading, setLoading] = useState(true);
  const [followups, setFollowups] = useState([]);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    loadData();
  }, [selectedPropertyId]);

  const loadData = async () => {
    try {
      const params = {};
      if (selectedPropertyId !== 'all') {
        params.propertyId = selectedPropertyId;
      }
      const [fuRes, convsRes] = await Promise.all([
        getFollowups(),
        getConversations(params)
      ]);
      setFollowups(fuRes.data);
      setConversations(convsRes.data);
    } catch (error) {
      console.error('Failed to load followups:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConversation = (convId) => conversations.find(c => c.id === convId);

  const handleMarkSent = async (fuId) => {
    try {
      await updateFollowupStatus(fuId, 'SENT');
      await loadData();
      toast.success('Follow-up marked as sent');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleSkip = async (fuId) => {
    try {
      await updateFollowupStatus(fuId, 'SKIPPED');
      await loadData();
      toast.success('Follow-up skipped');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const getDueLabel = (dueAt) => {
    const date = new Date(dueAt);
    if (isPast(date)) return { label: 'Overdue', class: 'bg-red-100 text-red-700' };
    if (isToday(date)) return { label: 'Today', class: 'bg-amber-100 text-amber-700' };
    if (isTomorrow(date)) return { label: 'Tomorrow', class: 'bg-blue-100 text-blue-700' };
    return { label: format(date, 'MMM d'), class: 'bg-stone-100 text-stone-700' };
  };

  // Group conversations by status for Kanban
  const getColumnData = (columnId) => {
    if (['FOLLOWUP_1', 'FOLLOWUP_2', 'CLOSE_LOOP'].includes(columnId)) {
      return followups.filter(fu => fu.type === columnId && fu.status === 'DUE')
        .map(fu => ({ ...fu, conversation: getConversation(fu.conversationId) }))
        .filter(fu => fu.conversation)
        .filter(fu => selectedPropertyId === 'all' || fu.conversation.propertyId === selectedPropertyId);
    }
    return conversations.filter(c => c.status === columnId);
  };

  if (loading) {
    return (
      <DeskLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-96 w-72 flex-shrink-0" />
            ))}
          </div>
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="followups-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Follow-ups</h1>
          <p className="text-stone-500 mt-1">Track and manage your follow-up pipeline</p>
        </div>

        {/* Kanban Board */}
        <div className="overflow-x-auto pb-4 -mx-2 px-2">
          <div className="flex gap-4 min-w-max pr-4">
            {columns.map((column) => {
              const items = getColumnData(column.id);
              return (
                <div 
                  key={column.id} 
                  className="w-72 flex-shrink-0"
                  data-testid={`column-${column.id.toLowerCase()}`}
                >
                  <Card className="border-stone-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${column.color}`} />
                          <CardTitle className="text-sm font-medium">{column.label}</CardTitle>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {items.length}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ScrollArea className="h-[calc(100vh-300px)]">
                        <div className="space-y-3 pr-2">
                          {items.map((item) => {
                            const conv = item.conversation || item;
                            const isFollowup = ['FOLLOWUP_1', 'FOLLOWUP_2', 'CLOSE_LOOP'].includes(column.id);
                            
                            return (
                              <Card 
                                key={item.id} 
                                className="p-4 border-stone-200 card-hover cursor-pointer"
                                data-testid={`kanban-card-${item.id}`}
                              >
                                <div className="space-y-2">
                                  <p className="font-medium text-stone-900 text-sm">
                                    {conv.guestName || conv.subject}
                                  </p>
                                  <p className="text-xs text-stone-500">
                                    {getPropertyName(conv.propertyId)}
                                  </p>
                                  
                                  {isFollowup && item.dueAt && (
                                    <div className="flex items-center gap-2">
                                      <Badge className={getDueLabel(item.dueAt).class}>
                                        <Calendar className="h-3 w-3 mr-1" />
                                        {getDueLabel(item.dueAt).label}
                                      </Badge>
                                    </div>
                                  )}
                                  
                                  {isFollowup && (
                                    <div className="flex gap-2 pt-2">
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="flex-1 h-8 text-xs rounded-full"
                                        onClick={() => handleMarkSent(item.id)}
                                        data-testid={`send-followup-${item.id}`}
                                      >
                                        <Check className="h-3 w-3 mr-1" /> Sent
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        className="h-8 text-xs"
                                        onClick={() => handleSkip(item.id)}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                  
                                  {!isFollowup && conv.updatedAt && (
                                    <p className="text-xs text-stone-400">
                                      {format(new Date(conv.updatedAt), 'MMM d, h:mm a')}
                                    </p>
                                  )}
                                </div>
                              </Card>
                            );
                          })}
                          
                          {items.length === 0 && (
                            <div className="text-center py-8 text-stone-400 text-sm">
                              No items
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DeskLayout>
  );
};

export default Followups;
